import { useEffect, useRef, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAudioExperience } from "../../../shared/AudioExperience";
import { useAuthSession } from "../../../shared/AuthSession";
import {
  CustomerButton as Button,
  CustomerLinkButton as Action,
} from "../../../shared/CustomerControls";
import { Icon } from "../../../shared/Icon";
import { usePageOwnsChrome, type MakingTask } from "../page-chrome";
import { taskHelpHref } from "../print/print-links";
import { BrandMark } from "../shared/BrandMark";
import { startBookHref } from "../voucher/voucher-session";
import "../shared/next-base.css";
import "./foundations.css";
import "./foundation-shell.css";

type HeaderOptions = {
  /**
   * The page's own "Start a book", where it has one (My Library's opens its choice of books).
   * Every other signed-in page links to where starting a book leads (C3).
   */
  action?: ReactNode;
  /** The voucher link when a page knows a nearer one ("Continue your voucher"). */
  voucher?: { to: string; label: string };
  /** The book and step being worked on: Help then opens about them (F8). */
  task?: MakingTask | null;
  /** The home page's own dark band: the same header in the front door's colours (C2). */
  dark?: boolean;
};

/**
 * The signed-in "making" header (W1, Stage 2 F1): My Library, Use your voucher, Help, Start a book
 * and the account menu, the same on every signed-in page (C2, 2026-11-02). Signed out, it offers
 * Sign in in the menu's place.
 */
function FoundationHeader({ action, voucher, task, dark = false }: HeaderOptions) {
  const auth = useAuthSession();
  const sound = useAudioExperience();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const menu = useRef<HTMLDetailsElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLElement | null>(null);
  // The account menu closes like the current one: Escape (returning to its button), a click
  // outside it, or focus moving past it.
  useEffect(() => {
    const details = menu.current;
    if (!details) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !details.open) return;
      details.open = false;
      details.querySelector("summary")?.focus();
    };
    const outside = (event: PointerEvent) => {
      if (details.open && !details.contains(event.target as Node)) details.open = false;
    };
    const leave = (event: FocusEvent) => {
      if (details.open && !details.contains(event.relatedTarget as Node | null))
        details.open = false;
    };
    document.addEventListener("keydown", escape);
    document.addEventListener("pointerdown", outside);
    details.addEventListener("focusout", leave);
    return () => {
      document.removeEventListener("keydown", escape);
      document.removeEventListener("pointerdown", outside);
      details.removeEventListener("focusout", leave);
    };
  }, [auth.tokens]);
  const closeMenu = () => {
    if (menu.current) menu.current.open = false;
  };
  const close = () => {
    dialog.current?.close();
    trigger.current?.focus();
  };
  return (
    <header className="foundation-header">
      <Link to="/app" className="brand-link" aria-label="Life’s Capsule · My Library">
        <BrandMark light={dark} />
      </Link>
      <nav aria-label="Customer navigation">
        <NavLink to="/app" end>
          My Library
        </NavLink>
        <NavLink to={voucher?.to ?? "/redeem"}>{voucher?.label ?? "Use your voucher"}</NavLink>
        <NavLink to={taskHelpHref(task)}>Help</NavLink>
        {auth.tokens &&
          (action ?? (
            // On the home page's dark band it takes the home page's own primary red (C2).
            <Action
              tone={dark ? "primary" : "secondary"}
              to={startBookHref(pathname)}
              icon={<Icon name="plus" size={17} />}
            >
              Start a book
            </Action>
          ))}
        {auth.tokens ? (
          <details className="account-menu" ref={menu}>
            <summary>
              <Icon name="account" size={18} />
              Account
              <Icon name="chevron" size={15} />
            </summary>
            <div>
              {auth.accountEmail && (
                <p>
                  <span>{auth.accountEmail}</span>
                </p>
              )}
              <Link to="/app/account" onClick={closeMenu}>
                Account settings
              </Link>
              <Link to="/app/photos" onClick={closeMenu}>
                Photo library
              </Link>
              {/* The optional interface sounds lived on the retired header; their switch stays here.
                  One name, its state said once by aria-pressed, and shown beside it (P2-28). */}
              <Button
                tone="quiet"
                className="sound-switch"
                aria-pressed={sound.enabled}
                onClick={sound.toggle}
                icon={<Icon name={sound.enabled ? "sound" : "soundOff"} size={17} />}
                iconAfter={
                  <span className="sound-switch__state" aria-hidden="true">
                    {sound.enabled ? "On" : "Off"}
                  </span>
                }
              >
                Interface sounds
              </Button>
              <Button
                tone="quiet"
                onClick={() => {
                  trigger.current = menu.current?.querySelector("summary") || null;
                  closeMenu();
                  dialog.current?.showModal();
                }}
              >
                Sign out
              </Button>
            </div>
          </details>
        ) : (
          <Link to="/sign-in">Sign in</Link>
        )}
      </nav>
      <dialog
        className="signout-dialog"
        ref={dialog}
        aria-labelledby="signout-title"
        aria-describedby="signout-consequence"
        onCancel={(event) => {
          event.preventDefault();
          close();
        }}
      >
        <h2 id="signout-title">Sign out of this browser?</h2>
        <p id="signout-consequence">You’ll need a new sign-in email to open your books again.</p>
        <div className="actions">
          <Button tone="secondary" autoFocus onClick={close}>
            Stay signed in
          </Button>
          <Button
            tone="primary"
            onClick={() => {
              dialog.current?.close();
              auth.signOut();
              navigate("/sign-in", { replace: true });
            }}
          >
            Sign out
          </Button>
        </div>
      </dialog>
    </header>
  );
}

/**
 * The making bar (Stage 2, F1): on a book's pages, the book, the step it's on and the way back to
 * My Library, in one slim line under the header.
 */
export function MakingBar({ task }: { task: MakingTask }) {
  const exit = task.exit ?? { to: "/app", label: "My Library" };
  return (
    <nav className="making-bar" aria-label="Your book">
      <Link className="making-bar__exit" to={exit.to}>
        <Icon name="arrowBack" size={16} />
        {/^back\b/iu.test(exit.label) ? null : <span className="sr-only">Back to </span>}
        {exit.label}
      </Link>
      {(task.kind || task.title || task.step) && (
        <p className="making-bar__where">
          {/* The kind of book, its title and its step, in that order, a dot between each. */}
          {[
            task.kind && <span key="kind">{task.kind}</span>,
            task.title && <strong key="title">{task.title}</strong>,
            task.step && <span key="step">{task.step}</span>,
          ]
            .filter(Boolean)
            .flatMap((part, index) =>
              index
                ? [
                    <span key={`dot-${index}`} aria-hidden="true">
                      ·
                    </span>,
                    part,
                  ]
                : [part],
            )}
        </p>
      )}
    </nav>
  );
}

/**
 * The one signed-in chrome for pages that don't draw the account pages' shell: the W1 header,
 * and the making bar when the page is about a book.
 */
export function MakingChrome({
  task,
  action,
  voucher,
  dark = false,
  bar = true,
}: HeaderOptions & {
  /** False where the page already shows the book and the way back (My Library's reader). */
  bar?: boolean;
}) {
  return (
    <div
      className={
        "customer-design-system foundation-shell making-chrome" +
        (dark ? " making-chrome--dark" : "")
      }
    >
      <FoundationHeader action={action} voucher={voucher} task={task} dark={dark} />
      {task && bar && <MakingBar task={task} />}
    </div>
  );
}

/** Header, page and footer for the redesigned account pages (W1). */
export function FoundationShell({
  children,
  task,
}: {
  children: ReactNode;
  /** The book this page is about, for the making bar and Help. */
  task?: MakingTask | null;
}) {
  // These pages also sit at addresses the shell shows its own header on, such as /sign-in.
  usePageOwnsChrome();
  return (
    <div className="customer-design-system foundation-shell">
      <FoundationHeader task={task} />
      {task && <MakingBar task={task} />}
      {children}
      <footer className="foundation-footer">
        <BrandMark />
        <span>Family memories, made into books.</span>
        <nav className="foundation-footer__links" aria-label="Policies">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </nav>
      </footer>
    </div>
  );
}
